import {JobApiDto} from "../../api/model/rest";
import * as moment from "moment";
import {none, Option, option} from "ts-option";
import DateUtils from "../../utils/DateUtils";
import JobData from "./JobData";
import AuthenticatedEntity from "../user/AuthenticatedEntity";
import Tag from "../tag/Tag";
import User from "../user/User";
import {getUser} from "../../store/view/user";
import JobAction from "./JobAction";
import {getJobStatus} from "../../store/view/jobStatus";
import SerialNumbersData from "./SerialNumbersData";

export default class Job {
	/**
	 * String using to search a job.
	 */
	public readonly searchString: string;

	/**
	 * Job object.
	 * @param {string} id identifier of the job.
	 * @param {string} statusId identifier of the job status.
	 * @param {JobData} data data of the job.
	 * @param {moment.Moment} creationDate lead creation date.
	 * @param {moment.Moment} updateDate lead last update date.
	 * @param {referenceNumber} referenceNumber reference number of job
	 * @param {AuthenticatedEntity} creator creator data
	 * @param {string[]} tags array with tags
	 * @param {Option<string>} agentId assigned job agent.
	 * @param {string[]} watchers watchers ids
	 * @param {Option<JobAction>} lastAction
	 * @param {Option<string>} leadId identifier of related lead.
	 * @param {Option<moment.Moment>} completedDateTime of the job.
	 * @param {Option<string>} quoteId identifier of related quote.
	 * @param {Option<string>} preferredMerchantBranchLocationId identifier of merchant branch location.
	 * @param {Option<string>} merchantBranchLocationId identifier of merchant branch location.
	 */
	constructor(public readonly id: string,
		public readonly statusId: string,
		public readonly data: JobData,
		public readonly creationDate: moment.Moment,
		public readonly updateDate: moment.Moment,
		public readonly referenceNumber: string,
		public readonly creator: AuthenticatedEntity,
		public readonly tags: Tag[],
		public readonly agentId: Option<string>,
		public readonly watchers: string[],
		public readonly lastAction: Option<JobAction>,
		public readonly leadId: Option<string>,
		public readonly completedDateTime: Option<moment.Moment>,
		public readonly quoteId: Option<string>,
		public readonly preferredMerchantBranchLocationId: Option<string>
	) {

		this.searchString = `${referenceNumber} ${creationDate.format(DateUtils.DATE_TIME_FORMAT)}`.toLowerCase();
	}

	static fromJson(json: JobApiDto): Job {
		const principalContactId = json.contactId;
		const serialNumbers = json.serialNumbers ? option(SerialNumbersData.fromJson(json.serialNumbers)) : none;
		const jobData = new JobData(
			principalContactId,
			option(json.description),
			option(json.extendedDescription),
			json.jobTypeId,
			option(json.siteId),
			serialNumbers,
			option(json.merchantId),
			json.partsOnly,
			json.allowBom,
			option(json.merchantBranchLocationId),
			option(json.invoicePath),
			option(json.warrantyRegistrationStatus)
		);
		const creator = AuthenticatedEntity.fromJson(json.creator);
		const tags = json.tags ? json.tags.map(i => new Tag(i)) : [];
		const watchers = json.watchers ? json.watchers : [];
		const lastAction = json.lastActivity ? option(JobAction.fromString(json.lastActivity)) : none;
		const leadId = json.leadId ? option(json.leadId) : none;
		const completedDateTime = json.completedDateTime ? option(moment(json.completedDateTime)) : none;
		const quoteId = json.quoteId ? option(json.quoteId) : none;
		return new Job(json.id, json.statusId, jobData, moment(json.creationDate), moment(json.updateDate),
			json.referenceNumber, creator, tags, option(json.agentId), watchers, lastAction, leadId,
			completedDateTime, quoteId, option(json.preferredMerchantBranchLocationId));
	}

	/**
	 * Get job Agent.
	 * @returns {Option<User>}
	 */
	getAgent(): Option<User> {
		return this.agentId.flatMap(getUser);
	}

	/**
	 * Get job status label.
	 * @param {boolean} labelWithDisabled add disabled description if true
	 * @returns {string}
	 */
	getStatusLabel(labelWithDisabled?: boolean): string {
		return getJobStatus(this.statusId).map(s => s.getStatusLabel(labelWithDisabled)).getOrElse("");
	}

	copy({
		id = this.id,
		statusId = this.statusId,
		data = this.data,
		creationDate = this.creationDate,
		updateDate = this.updateDate,
		referenceNumber = this.referenceNumber,
		creator = this.creator,
		tags = this.tags,
		agentId = this.agentId,
		watchers = this.watchers,
		lastAction = this.lastAction,
		leadId = this.leadId,
		completedDateTime = this.completedDateTime,
		quoteId = this.quoteId,
		preferredMerchantBranchLocationId = this.preferredMerchantBranchLocationId
	}): Job {
		return new Job(id,
			statusId,
			data,
			creationDate,
			updateDate,
			referenceNumber,
			creator,
			tags,
			agentId,
			watchers,
			lastAction,
			leadId,
			completedDateTime,
			quoteId,
			preferredMerchantBranchLocationId);
	}
}
